<template>
  <div>
    <v-progress-linear v-if="loadingDeposito" indeterminate class="mb-2" />
    <v-alert v-else-if="!depositoIdSeleccionado" type="warning" dense class="mb-3">
      Seleccioná un depósito en la configuración del usuario para ver los presupuestos.
    </v-alert>
    <v-row v-else no-gutters>
      <v-col cols="12" md="3" lg="2">
        <presupuesto-filters
          @updateFilters="setFilters"
          @clearFilter="clearFilters"
        />
      </v-col>
      <v-col cols="12" md="9" lg="10" class="pl-md-3">
    <div class="table-fixed-header-wrap">
    <v-data-table
      :headers="headers"
      fixed-header
      height="calc(100vh - 260px)"
      :items="items"
      :server-items-length="totalItems"
      :loading="loading"
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
      @update:options="fetch"
    >
    <template v-slot:item.cliente="{ item }">
      <span
        class="cliente-cell"
        :title="item.cliente ? item.cliente.razonSocial : ''"
      >{{ item.cliente ? item.cliente.razonSocial : "—" }}</span>
    </template>
    <template v-slot:item.vendedor="{ item }">
      {{ item.vendedor ? item.vendedor.nombre + ' ' + item.vendedor.apellido : "—" }}
    </template>
    <template v-slot:item.deposito="{ item }">
      {{ item.deposito ? item.deposito.nombre : "—" }}
    </template>
    <template v-slot:item.precioTotal="{ item }">
      ${{ Number(item.precioTotal || 0).toFixed(2) }}
    </template>
    <template v-slot:item.createdAt="{ item }">
      {{ formatFecha(item.createdAt) }}
    </template>
    <template v-slot:item.estado="{ item }">
      <v-chip small dark class="white--text" :color="colorChipEstadoPresupuesto(item.estado)">
        {{ labelEstadoPresupuesto(item.estado) }}
      </v-chip>
    </template>
    <template v-slot:item.actions="{ item }">
      <div class="table-actions-cell">
        <show-button icon="mdi-eye" @click="$emit('show', item)" />
        <!-- Solo permitir editar si el presupuesto no fue confirmado (pedido aún no creado) -->
        <edit-button
          v-if="presupuestoEditable(item)"
          @click="$emit('edit', item)"
        />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="descargarPDF(item)">
              <v-icon small color="red">mdi-file-pdf-box</v-icon>
            </v-btn>
          </template>
          <span>Descargar PDF</span>
        </v-tooltip>
      </div>
    </template>
  </v-data-table>
    </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EditButton, ShowButton } from "@dracul/common-frontend";
import PresupuestoProvider from "../../providers/PresupuestoProvider";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";
import { formatFechaVentas } from "../../utils/dateFormat";
import { labelEstadoPresupuesto, colorChipEstadoPresupuesto } from "../../utils/estadoLabels";
import PresupuestoFilters from "./PresupuestoFilters/PresupuestoFilters.vue";

export default {
  name: "PresupuestoList",
  components: { EditButton, ShowButton, PresupuestoFilters },
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      loadingDeposito: false,
      downloadingId: null,
      depositoIdSeleccionado: null,
      activeFilters: {
        estado: null,
        clienteNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
    };
  },
  mounted() {
    this.loadDepositoSeleccionado();
  },
  activated() {
    this.loadDepositoSeleccionado();
  },
  computed: {
    headers() {
      return [
        { text: "Nº", value: "numeroPresupuesto", width: 80 },
        { text: "Cliente", value: "cliente", width: 400, cellClass: "cell-cliente" },
        { text: "Vendedor", value: "vendedor", sortable: false },
        { text: "Depósito", value: "deposito" },
        { text: "Total", value: "precioTotal" },
        { text: "Estado", value: "estado" },
        { text: "Fecha de creación", value: "createdAt" },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          width: 140,
          cellClass: "cell-actions",
          class: "cell-actions",
        },
      ];
    },
  },
  methods: {
    /** true solo si el presupuesto puede editarse: estado CREADO. PEDIDO_CREADO = no editable. */
    presupuestoEditable(item) {
      const e = (item && item.estado) ? String(item.estado) : "";
      return e === "CREADO";
    },
    formatFecha(val) {
      return formatFechaVentas(val);
    },
    labelEstadoPresupuesto,
    colorChipEstadoPresupuesto,
    nombreArchivoPresupuesto(item) {
      const num = item.numeroPresupuesto != null ? item.numeroPresupuesto : "";
      const cliente = (item.cliente && item.cliente.razonSocial)
        ? String(item.cliente.razonSocial).replace(/[/\\:*?"<>|]/g, "").trim().slice(0, 40)
        : "cliente";
      const fechaStr = item.createdAt ? formatFechaVentas(item.createdAt).slice(0, 10) : new Date().toISOString().slice(0, 10);
      const partes = ["Presupuesto", num ? `N${num}` : "", cliente, fechaStr].filter(Boolean);
      return `${partes.join("_")}.pdf`;
    },
    // Implementación original basada en archivo en disco (se mantiene para referencia)
    async descargarPDFAlmacenado(item) {
      const id = item.id || item._id;
      if (!id || this.downloadingId) return;
      this.downloadingId = id;
      try {
        const r = await PresupuestoProvider.generarPDF(id);
        const pdfPath = r.data && r.data.presupuestoGenerarPDF;
        if (!pdfPath) return;
        const tokenRes = await PresupuestoProvider.presupuestoDownloadToken(id);
        const token = tokenRes?.data?.presupuestoDownloadToken;
        if (!token) throw new Error("No se pudo obtener el token de descarga");
        const base = (process.env.VUE_APP_APIHOST && process.env.VUE_APP_APIHOST.replace(/\/$/, "")) || window.location.origin;
        const url = `${base}${pdfPath}?token=${encodeURIComponent(token)}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error("Error al obtener el PDF");
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = this.nombreArchivoPresupuesto(item);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingId = null;
      }
    },
    // POC: genera el PDF en memoria (base64) y lo descarga directamente
    async descargarPDF(item) {
      const id = item.id || item._id;
      if (!id || this.downloadingId) return;
      this.downloadingId = id;
      try {
        const r = await PresupuestoProvider.generarPDFBase64(id);
        const base64 = r.data && r.data.presupuestoGenerarPDFBase64;
        if (!base64) return;

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = this.nombreArchivoPresupuesto(item);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingId = null;
      }
    },
    async loadDepositoSeleccionado() {
      this.loadingDeposito = true;
      try {
        let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          const r = await UserConfigurationProvider.getUserConfiguration(true);
          const config = r.data && r.data.userConfigurationGet;
          depositoId = (config && config.depositoIdSeleccionado) || (config && config.deposito && config.deposito.id) || null;
        }
        this.depositoIdSeleccionado = depositoId;
        if (depositoId) this.fetch();
        else {
          this.items = [];
          this.totalItems = 0;
        }
      } catch (e) {
        console.error(e);
        this.depositoIdSeleccionado = null;
        this.items = [];
        this.totalItems = 0;
      } finally {
        this.loadingDeposito = false;
      }
    },
    setFilters(filters) {
      this.options.page = 1;
      this.activeFilters = { ...filters };
      this.fetch();
    },
    clearFilters() {
      this.options.page = 1;
      this.activeFilters = {
        estado: null,
        clienteNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      };
      this.fetch();
    },
    fetch() {
      if (!this.depositoIdSeleccionado) {
        this.items = [];
        this.totalItems = 0;
        return;
      }
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const orderBy = sortBy && sortBy[0] ? sortBy[0] : "createdAt";
      const orderDesc = sortDesc && sortDesc[0] !== undefined ? sortDesc[0] : true;
      const filters = { depositoId: this.depositoIdSeleccionado, ...this.activeFilters };
      PresupuestoProvider.paginatePresupuestos(page, itemsPerPage, null, orderBy, orderDesc, filters)
        .then((r) => {
          this.items = (r.data && r.data.presupuestoPaginate && r.data.presupuestoPaginate.items) || [];
          this.totalItems = (r.data && r.data.presupuestoPaginate && r.data.presupuestoPaginate.totalItems) || 0;
        })
        .catch((err) => console.error(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}

.cliente-cell {
  display: block;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .cell-cliente {
  max-width: 400px;
}
</style>
